// ═══════════════════════════════════════════════════════════════════════════════
// ARTIFACT - Recherche Posts Réseau (LIVE DATA)
// Affiche les posts du réseau social avec interactions
// Coût: 0 crédit - Requête DB uniquement
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Heart, MessageCircle, Share2, Bookmark, ExternalLink,
  MoreHorizontal, MapPin, Clock, TrendingUp,
  Eye, Award, Search, Loader2, RefreshCw, Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function ArtifactSearchPosts({ 
  initialQuery = '',
  onLike,
  onComment,
  onShare,
  onSave
}) {
  const navigate = useNavigate();
  
  // États
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [filter, setFilter] = useState('recent');
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FETCH DATA
  // ═══════════════════════════════════════════════════════════════════════════
  
  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      params.append('sort_by', filter);
      params.append('limit', '20');
      
      const response = await fetch(`${API_URL}/api/search/posts?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }
      
      const data = await response.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Search error:', err);
      setError('Impossible de charger les publications. Réessaie.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filter]);
  
  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleLike = (postId) => {
    setResults(prev => prev.map(p => 
      p.id === postId 
        ? { ...p, isLiked: !p.isLiked, likes: p.isLiked ? p.likes - 1 : p.likes + 1 }
        : p
    ));
    if (onLike) onLike(postId);
  };
  
  const handleSave = (postId) => {
    setResults(prev => prev.map(p => 
      p.id === postId ? { ...p, isSaved: !p.isSaved } : p
    ));
    if (onSave) onSave(postId);
  };
  
  const handleViewPost = (post) => {
    navigate(`/reseau/post/${post.id}`);
  };
  
  const handleViewProfile = (author) => {
    navigate(`/reseau/profil/${author.id}`);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchPosts();
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) return 'À l\'instant';
    if (hours < 24) return `Il y a ${hours}h`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;
    return date.toLocaleDateString('fr-FR');
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3">
        {/* Barre de recherche */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher une publication, #hashtag..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8a] transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-semibold text-gray-900">
              {loading ? 'Recherche...' : `${total} publication${total > 1 ? 's' : ''}`}
            </h3>
          </div>
          <button
            onClick={fetchPosts}
            disabled={loading}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {/* Filtres */}
        <div className="flex gap-2">
          {[
            { id: 'recent', label: 'Récents', icon: Clock },
            { id: 'popular', label: 'Populaires', icon: TrendingUp },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filter === f.id
                  ? 'bg-[#1E3A5F] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Posts */}
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <div className="text-center py-8">
            <p className="text-red-500 mb-2">{error}</p>
            <button
              onClick={fetchPosts}
              className="text-sm text-[#1E3A5F] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : loading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white p-4 animate-pulse">
                <div className="flex gap-3 mb-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-1" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucune publication trouvée</p>
            <button
              onClick={() => setSearchQuery('')}
              className="mt-2 text-sm text-[#1E3A5F] hover:underline"
            >
              Voir toutes les publications
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {results.map((post, idx) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-white p-4"
              >
                {/* Author */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => handleViewProfile(post.author)}
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    {post.author?.avatar ? (
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center text-white font-bold">
                        {post.author?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <div className="text-left">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold text-gray-900 text-sm">
                          {post.author?.name || 'Utilisateur'}
                        </span>
                        {post.author?.verified && (
                          <Award className="w-3.5 h-3.5 text-blue-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {post.author?.metier && <span>{post.author.metier}</span>}
                        {post.author?.metier && post.created_at && <span>•</span>}
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                  </button>
                  
                  <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                    <MoreHorizontal className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
                
                {/* Content */}
                <p className="text-sm text-gray-800 mb-3 whitespace-pre-wrap">{post.content}</p>
                
                {/* Images */}
                {post.images && post.images.length > 0 && (
                  <div className={`mb-3 rounded-xl overflow-hidden ${
                    post.images.length === 1 ? '' :
                    post.images.length === 2 ? 'grid grid-cols-2 gap-1' :
                    'grid grid-cols-2 gap-1'
                  }`}>
                    {post.images.slice(0, 4).map((img, i) => (
                      <div
                        key={i}
                        className={`relative ${
                          post.images.length === 1 ? 'aspect-video' :
                          post.images.length === 3 && i === 0 ? 'row-span-2 aspect-square' :
                          'aspect-square'
                        }`}
                      >
                        <img
                          src={img}
                          alt=""
                          className="w-full h-full object-cover cursor-pointer hover:opacity-95 transition-opacity"
                          onClick={() => handleViewPost(post)}
                        />
                        {i === 3 && post.images.length > 4 && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-bold">
                            +{post.images.length - 4}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {post.tags.map(tag => (
                      <span 
                        key={tag} 
                        className="text-xs text-blue-600 hover:underline cursor-pointer"
                        onClick={() => setSearchQuery(`#${tag}`)}
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                  {post.views > 0 && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" />
                      {post.views} vue{post.views > 1 ? 's' : ''}
                    </span>
                  )}
                  {post.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {post.location}
                    </span>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
                      post.isLiked
                        ? 'text-red-500 bg-red-50'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Heart className={`w-4 h-4 ${post.isLiked ? 'fill-current' : ''}`} />
                    <span className="text-sm">{post.likes || 0}</span>
                  </button>
                  
                  <button
                    onClick={() => handleViewPost(post)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-sm">{post.comments || 0}</span>
                  </button>
                  
                  <button
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm">{post.shares || 0}</span>
                  </button>
                  
                  <button
                    onClick={() => handleSave(post.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors ${
                      post.isSaved
                        ? 'text-[#1E3A5F] bg-[#1E3A5F]/10'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Bookmark className={`w-4 h-4 ${post.isSaved ? 'fill-current' : ''}`} />
                  </button>
                  
                  <button
                    onClick={() => handleViewPost(post)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[#1E3A5F] hover:bg-[#1E3A5F]/10 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
